import { lazy, Suspense, useEffect, useState, type ComponentType } from "react";
import { AuthPage, OrganizationActivationPage, useAuth } from "./auth";
import { Spinner } from "./components";
import { Shell } from "./Shell";
import type { SupplierListingSection } from "./pages/InventoryPage";
import type { ImportsDeveloperSection } from "./pages/ImportsDeveloperPage";
import type { FinanceSection } from "./pages/FinancePage";
import type { ReportsSection } from "./pages/ReportsPage";
import type { SecuritySection } from "./pages/SecurityPage";
import type { OperationsSection } from "./pages/OperationsPage";
import type { GovernanceSection } from "./pages/GovernancePages";
import type { CatalogSection } from "./pages/CatalogAdminPage";
import type { SystemAdminSection } from "./pages/SystemAdminPage";
import { canAccessPath } from "./access";

function lazyNamed<T extends Record<string, unknown>>(loader: () => Promise<T>, name: keyof T) {
  return lazy(async () => ({ default: (await loader())[name] as ComponentType<any> }));
}

const DashboardPage = lazyNamed(() => import("./pages/DashboardPage"), "DashboardPage");
const MarketplacePage = lazyNamed(() => import("./pages/MarketplacePage"), "MarketplacePage");
const MarketplaceListingPage = lazyNamed(() => import("./pages/MarketplacePage"), "MarketplaceListingPage");
const MarketplacePurchasePage = lazyNamed(() => import("./pages/MarketplacePage"), "MarketplacePurchasePage");
const OrdersPage = lazyNamed(() => import("./pages/OrdersPage"), "OrdersPage");
const OrderDetailPage = lazyNamed(() => import("./pages/OrdersPage"), "OrderDetailPage");
const OrderDeliveriesPage = lazyNamed(() => import("./pages/OrdersPage"), "OrderDeliveriesPage");
const OrderPushPage = lazyNamed(() => import("./pages/OrderActionPages"), "OrderPushPage");
const OrderRefundPage = lazyNamed(() => import("./pages/OrderActionPages"), "OrderRefundPage");
const OrderExtractionPage = lazyNamed(() => import("./pages/OrderActionPages"), "OrderExtractionPage");
const OrderBlockPlatformPage = lazyNamed(() => import("./pages/OrderActionPages"), "OrderBlockPlatformPage");
const InquiryResponsePage = lazyNamed(() => import("./pages/OrderActionPages"), "InquiryResponsePage");
const InquiryPurchasePage = lazyNamed(() => import("./pages/OrderActionPages"), "InquiryPurchasePage");
const InventoryPage = lazyNamed(() => import("./pages/InventoryPage"), "InventoryPage");
const SupplierListingCreatePage = lazyNamed(() => import("./pages/InventoryPage"), "SupplierListingCreatePage");
const SupplierListingPage = lazyNamed(() => import("./pages/InventoryPage"), "SupplierListingPage");
const ImportsDeveloperPage = lazyNamed(() => import("./pages/ImportsDeveloperPage"), "ImportsDeveloperPage");
const ImportCreatePage = lazyNamed(() => import("./pages/DeveloperActionPages"), "ImportCreatePage");
const APIClientCreatePage = lazyNamed(() => import("./pages/DeveloperActionPages"), "APIClientCreatePage");
const WebhookCreatePage = lazyNamed(() => import("./pages/DeveloperActionPages"), "WebhookCreatePage");
const IntegrationsPage = lazyNamed(() => import("./pages/IntegrationsPage"), "IntegrationsPage");
const IntegrationCreatePage = lazyNamed(() => import("./pages/IntegrationsPage"), "IntegrationCreatePage");
const IntegrationEditPage = lazyNamed(() => import("./pages/IntegrationsPage"), "IntegrationEditPage");
const IntegrationDetailPage = lazyNamed(() => import("./pages/IntegrationsPage"), "IntegrationDetailPage");
const AutomationPage = lazyNamed(() => import("./pages/AutomationPage"), "AutomationPage");
const AutomationCreatePage = lazyNamed(() => import("./pages/AutomationPage"), "AutomationCreatePage");
const AutomationDetailPage = lazyNamed(() => import("./pages/AutomationPage"), "AutomationDetailPage");
const AutomationEditPage = lazyNamed(() => import("./pages/AutomationPage"), "AutomationEditPage");
const AutomationExecutionsPage = lazyNamed(() => import("./pages/AutomationPage"), "AutomationExecutionsPage");
const FinancePage = lazyNamed(() => import("./pages/FinancePage"), "FinancePage");
const PaymentCreatePage = lazyNamed(() => import("./pages/FinanceActionPages"), "PaymentCreatePage");
const WithdrawalAccountCreatePage = lazyNamed(() => import("./pages/FinanceActionPages"), "WithdrawalAccountCreatePage");
const WithdrawalCreatePage = lazyNamed(() => import("./pages/FinanceActionPages"), "WithdrawalCreatePage");
const BalanceAdjustmentCreatePage = lazyNamed(() => import("./pages/FinanceActionPages"), "BalanceAdjustmentCreatePage");
const CommissionRuleCreatePage = lazyNamed(() => import("./pages/FinanceActionPages"), "CommissionRuleCreatePage");
const InvoiceCreatePage = lazyNamed(() => import("./pages/FinanceActionPages"), "InvoiceCreatePage");
const PaymentRefundRequestPage = lazyNamed(() => import("./pages/FinanceProcessPages"), "PaymentRefundRequestPage");
const PaymentCancelPage = lazyNamed(() => import("./pages/FinanceProcessPages"), "PaymentCancelPage");
const PaymentRefundReviewPage = lazyNamed(() => import("./pages/FinanceProcessPages"), "PaymentRefundReviewPage");
const BalanceAdjustmentReviewPage = lazyNamed(() => import("./pages/FinanceProcessPages"), "BalanceAdjustmentReviewPage");
const InvoiceProcessPage = lazyNamed(() => import("./pages/FinanceProcessPages"), "InvoiceProcessPage");
const WithdrawalPayoutPage = lazyNamed(() => import("./pages/FinanceProcessPages"), "WithdrawalPayoutPage");
const CommissionPayoutPage = lazyNamed(() => import("./pages/FinanceProcessPages"), "CommissionPayoutPage");
const ReportsPage = lazyNamed(() => import("./pages/ReportsPage"), "ReportsPage");
const ReconciliationDetailPage = lazyNamed(() => import("./pages/ReportsPage"), "ReconciliationDetailPage");
const ReconciliationCreatePage = lazyNamed(() => import("./pages/ReportsActionPages"), "ReconciliationCreatePage");
const ReconciliationDifferenceResolvePage = lazyNamed(() => import("./pages/ReportsActionPages"), "ReconciliationDifferenceResolvePage");
const SupportPage = lazyNamed(() => import("./pages/SupportPage"), "SupportPage");
const TicketDetailPage = lazyNamed(() => import("./pages/SupportPage"), "TicketDetailPage");
const TicketCreatePage = lazyNamed(() => import("./pages/SupportActionPages"), "TicketCreatePage");
const SecurityPage = lazyNamed(() => import("./pages/SecurityPage"), "SecurityPage");
const PasswordChangePage = lazyNamed(() => import("./pages/SecurityActionPages"), "PasswordChangePage");
const MFASetupPage = lazyNamed(() => import("./pages/SecurityActionPages"), "MFASetupPage");
const MFADisablePage = lazyNamed(() => import("./pages/SecurityActionPages"), "MFADisablePage");
const MemberInvitePage = lazyNamed(() => import("./pages/SecurityActionPages"), "MemberInvitePage");
const MemberEditPage = lazyNamed(() => import("./pages/SecurityActionPages"), "MemberEditPage");
const OperationsPage = lazyNamed(() => import("./pages/OperationsPage"), "OperationsPage");
const CustomerProfilePage = lazyNamed(() => import("./pages/OperationsPage"), "CustomerProfilePage");
const OperatorOrderDeliveryDetailPage = lazyNamed(() => import("./pages/OperationsPage"), "OperatorOrderDeliveryDetailPage");
const GovernancePage = lazyNamed(() => import("./pages/GovernancePages"), "GovernancePage");
const ManagedPlatformCreatePage = lazyNamed(() => import("./pages/OperationsActionPages"), "ManagedPlatformCreatePage");
const ManagedPlatformAccountCreatePage = lazyNamed(() => import("./pages/OperationsActionPages"), "ManagedPlatformAccountCreatePage");
const ManagedSupplierCreatePage = lazyNamed(() => import("./pages/OperationsActionPages"), "ManagedSupplierCreatePage");
const AssignmentCreatePage = lazyNamed(() => import("./pages/OperationsActionPages"), "AssignmentCreatePage");
const TeamMemberCreatePage = lazyNamed(() => import("./pages/OperationsActionPages"), "TeamMemberCreatePage");
const TeamMemberEditPage = lazyNamed(() => import("./pages/OperationsActionPages"), "TeamMemberEditPage");
const ReviewActionPage = lazyNamed(() => import("./pages/OperationsActionPages"), "ReviewActionPage");
const CatalogAdminPage = lazyNamed(() => import("./pages/CatalogAdminPage"), "CatalogAdminPage");
const ProviderCreatePage = lazyNamed(() => import("./pages/CatalogActionPages"), "ProviderCreatePage");
const CredentialTypeCreatePage = lazyNamed(() => import("./pages/CatalogActionPages"), "CredentialTypeCreatePage");
const ModelCreatePage = lazyNamed(() => import("./pages/CatalogActionPages"), "ModelCreatePage");
const PricingRuleCreatePage = lazyNamed(() => import("./pages/CatalogActionPages"), "PricingRuleCreatePage");
const CreditPage = lazyNamed(() => import("./pages/CreditPage"), "CreditPage");
const CreditAppealPage = lazyNamed(() => import("./pages/TrustInventoryActionPages"), "CreditAppealPage");
const MarketplaceBlockSupplierPage = lazyNamed(() => import("./pages/TrustInventoryActionPages"), "MarketplaceBlockSupplierPage");
const CredentialEditPage = lazyNamed(() => import("./pages/TrustInventoryActionPages"), "CredentialEditPage");
const SystemAdminPage = lazyNamed(() => import("./pages/SystemAdminPage"), "SystemAdminPage");
const SystemUserStatusPage = lazyNamed(() => import("./pages/SystemActionPages"), "SystemUserStatusPage");
const SystemOrganizationStatusPage = lazyNamed(() => import("./pages/SystemActionPages"), "SystemOrganizationStatusPage");
const SystemOrganizationCreditPage = lazyNamed(() => import("./pages/SystemActionPages"), "SystemOrganizationCreditPage");
const AccountProfilePage = lazyNamed(() => import("./pages/AccountPages"), "AccountProfilePage");
const AccountEmailPage = lazyNamed(() => import("./pages/AccountPages"), "AccountEmailPage");
const SupplyDemandPage = lazyNamed(() => import("./pages/SupplyDemandPage"), "SupplyDemandPage");

export function navigate(path: string) {
  if (window.location.pathname === path) return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function usePathname() {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const update = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  return pathname;
}

export function App() {
  const { subject, loading } = useAuth();
  const pathname = usePathname();
  if (loading) return <div className="boot"><div className="brand-mark">KM</div><Spinner /><span>建立安全会话</span></div>;
  if (!subject || ["/verify-email", "/reset-password", "/accept-invitation"].includes(pathname)) return <AuthPage />;
  const fallback = defaultPath(subject.organization_type);
	if (!subject.email_verified || subject.organization_status !== "active") {
    if (pathname !== "/activation") {
      queueMicrotask(() => navigate("/activation"));
      return null;
    }
    return <OrganizationActivationPage />;
  }
  if (pathname === "/activation") {
    queueMicrotask(() => navigate(fallback));
    return null;
  }

  if (pathname === "/finance" && sessionStorage.getItem("keymarket.finance.return") === "/finance/topups") {
    sessionStorage.removeItem("keymarket.finance.return");
    queueMicrotask(() => navigate("/finance/topups"));
    return null;
  }

  const pages: Record<string, React.ReactNode> = {
    "/dashboard": <DashboardPage />,
    "/market": <MarketplacePage />,
    "/supply-demand": <SupplyDemandPage />,
    "/orders": <OrdersPage section="orders" />,
    "/inquiries": <OrdersPage section="inquiries" />,
    "/inventory": <InventoryPage />,
    ...Object.fromEntries((["imports", "clients", "webhooks", "webhook-deliveries", "proofs"] as ImportsDeveloperSection[])
      .map((section) => [`/developer/${section}`, <ImportsDeveloperPage key={section} section={section} />])),
    "/developer/imports/new": <ImportCreatePage />,
    "/developer/clients/new": <APIClientCreatePage />,
    "/developer/webhooks/new": <WebhookCreatePage />,
    "/integrations": <IntegrationsPage />,
    "/automation": <AutomationPage />,
    ...Object.fromEntries((["overview", "balances", "adjustments", "topups", "activity", "refunds", "settlements", "withdrawals", "accounts", "commissions", "platform-revenue", "invoices"] as FinanceSection[])
      .map((section) => [section === "overview" ? "/finance" : `/finance/${section}`, <FinancePage key={section} section={section} />])),
    "/finance/topups/new": <PaymentCreatePage />,
    "/finance/accounts/new": <WithdrawalAccountCreatePage />,
    "/finance/withdrawals/new": <WithdrawalCreatePage />,
    "/finance/adjustments/new": <BalanceAdjustmentCreatePage />,
    "/finance/commissions/new": <CommissionRuleCreatePage />,
    "/finance/invoices/new": <InvoiceCreatePage />,
    ...Object.fromEntries((["overview", "collection-health", "usage", "reconciliations"] as ReportsSection[])
      .map((section) => [section === "overview" ? "/reports" : `/reports/${section}`, <ReportsPage key={section} section={section} />])),
    "/reports/reconciliations/new": <ReconciliationCreatePage />,
    "/support": <SupportPage />,
    "/support/new": <TicketCreatePage />,
    "/credit": <CreditPage />,
    "/credit/appeal": <CreditAppealPage />,
    ...Object.fromEntries((["account", "sessions", "members", "invitations", "blacklists"] as SecuritySection[])
      .map((section) => [section === "account" ? "/security" : `/security/${section}`, <SecurityPage key={section} section={section} />])),
    "/security/password": <PasswordChangePage />,
    "/account/profile": <AccountProfilePage />,
    "/account/email": <AccountEmailPage />,
    "/security/mfa/setup": <MFASetupPage />,
    "/security/mfa/disable": <MFADisablePage />,
    "/security/invitations/new": <MemberInvitePage />,
    ...Object.fromEntries((["platforms", "suppliers", "managed-platforms", "assignments", "deliveries", "reviews", "team", "audit"] as OperationsSection[])
      .map((section) => [`/operations/${section}`, <OperationsPage key={section} section={section} />])),
    ...Object.fromEntries((["inventory", "targets", "hosted-newapi"] as GovernanceSection[])
      .map((section) => [`/operations/${section}`, <GovernancePage key={section} section={section} />])),
    ...Object.fromEntries((["providers", "credential-types", "models", "pricing"] as CatalogSection[])
      .map((section) => [`/catalog/${section}`, <CatalogAdminPage key={section} section={section} />])),
    "/catalog/providers/new": <ProviderCreatePage />,
    "/catalog/credential-types/new": <CredentialTypeCreatePage />,
    "/catalog/models/new": <ModelCreatePage />,
    "/catalog/pricing/new": <PricingRuleCreatePage />,
    ...Object.fromEntries((["overview", "users", "organizations", "operators", "payments", "mail", "audit", "health"] as SystemAdminSection[])
      .map((section) => [section === "overview" ? "/system" : `/system/${section}`, <SystemAdminPage key={section} section={section} />])),
    "/system/operators/new": <TeamMemberCreatePage workspace="system" />,
  };
  if (pathname === "/operations") {
    const legacy = new URLSearchParams(window.location.search);
    const target = legacy.get("tab") === "delivery"
      ? legacy.get("focus") === "reviews" ? "/operations/reviews" : "/operations/deliveries"
      : legacy.get("tab") === "governance" ? "/operations/team" : "/operations/platforms";
    const status = legacy.get("status");
    queueMicrotask(() => navigate(status && target === "/operations/deliveries" ? `${target}?status=${encodeURIComponent(status)}` : target));
    return null;
  }
  if (pathname === "/reports") {
    const legacy = new URLSearchParams(window.location.search);
    if (legacy.get("focus") === "reconciliations") {
      queueMicrotask(() => navigate("/reports/reconciliations"));
      return null;
    }
  }
  if (pathname === "/system-admin") {
    queueMicrotask(() => navigate("/system"));
    return null;
  }
  if (pathname === "/catalog-admin") {
    queueMicrotask(() => navigate("/catalog/providers"));
    return null;
  }
  if (pathname === "/developer") {
    queueMicrotask(() => navigate("/developer/imports"));
    return null;
  }
  if (pathname === "/" || pathname === "/login") {
    queueMicrotask(() => navigate(fallback));
    return null;
  }
  let page = pages[pathname];
  const actionPermissionPaths: Record<string, string> = {
    "/finance/topups/new": "/finance/topups",
    "/finance/accounts/new": "/finance/accounts",
    "/finance/withdrawals/new": "/finance/withdrawals",
    "/finance/adjustments/new": "/finance/adjustments",
    "/finance/commissions/new": "/finance/commissions",
    "/finance/invoices/new": "/finance/invoices",
    "/support/new": "/support",
    "/developer/imports/new": "/developer/imports",
    "/developer/clients/new": "/developer/clients",
    "/developer/webhooks/new": "/developer/webhooks",
    "/reports/reconciliations/new": "/reports",
    "/credit/appeal": "/credit",
    "/account/profile": "/security",
    "/account/email": "/security",
    "/system/operators/new": "/system",
  };
  let permissionPath = actionPermissionPaths[pathname] ?? (pathname.startsWith("/catalog/") ? "/catalog-admin"
    : pathname.startsWith("/reports/") ? "/reports"
    : pathname.startsWith("/security/") ? "/security"
    : pathname === "/inquiries" ? "/orders" : pathname);
  const marketBlockMatch = pathname.match(/^\/market\/([^/]+)\/block$/);
  const marketPurchaseMatch = pathname.match(/^\/market\/([^/]+)\/purchase$/);
  const marketListingMatch = pathname.match(/^\/market\/([^/]+)$/);
  const integrationEditMatch = pathname.match(/^\/integrations\/([^/]+)\/edit$/);
  const integrationDetailMatch = pathname.match(/^\/integrations\/([^/]+)$/);
  const automationExecutionsMatch = pathname.match(/^\/automation\/([^/]+)\/executions$/);
  const automationEditMatch = pathname.match(/^\/automation\/([^/]+)\/edit$/);
  const automationDetailMatch = pathname.match(/^\/automation\/([^/]+)$/);
  const inquiryResponseMatch = pathname.match(/^\/inquiries\/([^/]+)\/respond$/);
  const inquiryPurchaseMatch = pathname.match(/^\/inquiries\/([^/]+)\/purchase$/);
  const orderPushMatch = pathname.match(/^\/orders\/([^/]+)\/push$/);
  const orderRefundMatch = pathname.match(/^\/orders\/([^/]+)\/refund$/);
  const orderExtractionMatch = pathname.match(/^\/orders\/([^/]+)\/allocations\/([^/]+)\/extract$/);
  const orderBlockPlatformMatch = pathname.match(/^\/orders\/([^/]+)\/block-platform$/);
  const orderDeliveriesMatch = pathname.match(/^\/orders\/([^/]+)\/deliveries$/);
  const orderDetailMatch = pathname.match(/^\/orders\/([^/]+)$/);
  const operatorDeliveryDetailMatch = pathname.match(/^\/operations\/deliveries\/([^/]+)$/);
  const ticketDetailMatch = pathname.match(/^\/support\/([^/]+)$/);
  const reconciliationMatch = pathname.match(/^\/reports\/reconciliations\/([^/]+)$/);
  const reconciliationDifferenceResolveMatch = pathname.match(/^\/reports\/reconciliations\/([^/]+)\/differences\/([^/]+)\/resolve$/);
  const customerProfileMatch = pathname.match(/^\/operations\/(platforms|suppliers)\/([^/]+)$/);
  const teamMemberEditMatch = pathname.match(/^\/operations\/team\/([^/]+)\/edit$/);
  const reviewActionMatch = pathname.match(/^\/operations\/reviews\/([^/]+)\/([^/]+)$/);
  const paymentRefundRequestMatch = pathname.match(/^\/finance\/topups\/([^/]+)\/refund$/);
  const paymentCancelMatch = pathname.match(/^\/finance\/topups\/([^/]+)\/cancel$/);
  const paymentRefundReviewMatch = pathname.match(/^\/finance\/refunds\/([^/]+)\/(complete|reject)$/);
  const adjustmentReviewMatch = pathname.match(/^\/finance\/adjustments\/([^/]+)\/(approve|reject)$/);
  const invoiceProcessMatch = pathname.match(/^\/finance\/invoices\/([^/]+)\/process$/);
  const withdrawalPayoutMatch = pathname.match(/^\/finance\/withdrawals\/([^/]+)\/payout$/);
  const commissionPayoutMatch = pathname.match(/^\/finance\/commissions\/([^/]+)\/payout$/);
  const credentialEditMatch = pathname.match(/^\/inventory\/([^/]+)\/keys\/([^/]+)\/edit$/);
  const securityMemberEditMatch = pathname.match(/^\/security\/members\/([^/]+)\/edit$/);
  const systemUserStatusMatch = pathname.match(/^\/system\/users\/([^/]+)\/status$/);
  const systemOrganizationActionMatch = pathname.match(/^\/system\/organizations\/([^/]+)\/(status|credit)$/);
  const systemOperatorEditMatch = pathname.match(/^\/system\/operators\/([^/]+)\/edit$/);
  if (pathname === "/integrations/new") {
    page = <IntegrationCreatePage />;
    permissionPath = "/integrations";
  } else if (integrationEditMatch?.[1]) {
    page = <IntegrationEditPage key={`edit:${integrationEditMatch[1]}`} targetID={decodeURIComponent(integrationEditMatch[1])} />;
    permissionPath = "/integrations";
  } else if (integrationDetailMatch?.[1] && integrationDetailMatch[1] !== "new") {
    page = <IntegrationDetailPage key={`detail:${integrationDetailMatch[1]}`} targetID={decodeURIComponent(integrationDetailMatch[1])} />;
    permissionPath = "/integrations";
  } else if (pathname === "/automation/new") {
    page = <AutomationCreatePage />;
    permissionPath = "/automation";
  } else if (automationExecutionsMatch?.[1]) {
    page = <AutomationExecutionsPage key={`executions:${automationExecutionsMatch[1]}`} policyID={decodeURIComponent(automationExecutionsMatch[1])} />;
    permissionPath = "/automation";
  } else if (automationEditMatch?.[1]) {
    page = <AutomationEditPage key={`edit:${automationEditMatch[1]}`} policyID={decodeURIComponent(automationEditMatch[1])} />;
    permissionPath = "/automation";
  } else if (automationDetailMatch?.[1] && automationDetailMatch[1] !== "new") {
    page = <AutomationDetailPage key={`detail:${automationDetailMatch[1]}`} policyID={decodeURIComponent(automationDetailMatch[1])} />;
    permissionPath = "/automation";
  } else if (marketBlockMatch?.[1]) {
    page = <MarketplaceBlockSupplierPage key={`block:${marketBlockMatch[1]}`} listingID={decodeURIComponent(marketBlockMatch[1])} />;
    permissionPath = "/market";
  } else if (marketPurchaseMatch?.[1]) {
    page = <MarketplacePurchasePage key={`purchase:${marketPurchaseMatch[1]}`} listingID={decodeURIComponent(marketPurchaseMatch[1])} />;
    permissionPath = "/market";
  } else if (marketListingMatch?.[1]) {
    page = <MarketplaceListingPage key={`detail:${marketListingMatch[1]}`} listingID={decodeURIComponent(marketListingMatch[1])} />;
    permissionPath = "/market";
  } else if (inquiryResponseMatch?.[1]) {
    page = <InquiryResponsePage key={`respond:${inquiryResponseMatch[1]}`} inquiryID={decodeURIComponent(inquiryResponseMatch[1])} />;
    permissionPath = "/orders";
  } else if (inquiryPurchaseMatch?.[1]) {
    page = <InquiryPurchasePage key={`purchase:${inquiryPurchaseMatch[1]}`} inquiryID={decodeURIComponent(inquiryPurchaseMatch[1])} />;
    permissionPath = "/orders";
  } else if (orderPushMatch?.[1]) {
    page = <OrderPushPage key={`push:${orderPushMatch[1]}`} orderID={decodeURIComponent(orderPushMatch[1])} />;
    permissionPath = "/orders";
  } else if (orderRefundMatch?.[1]) {
    page = <OrderRefundPage key={`refund:${orderRefundMatch[1]}`} orderID={decodeURIComponent(orderRefundMatch[1])} />;
    permissionPath = "/orders";
  } else if (orderExtractionMatch?.[1] && orderExtractionMatch[2]) {
    page = <OrderExtractionPage key={`extract:${orderExtractionMatch[1]}:${orderExtractionMatch[2]}`} orderID={decodeURIComponent(orderExtractionMatch[1])} allocationID={decodeURIComponent(orderExtractionMatch[2])} />;
    permissionPath = "/orders";
  } else if (orderBlockPlatformMatch?.[1]) {
    page = <OrderBlockPlatformPage key={`block-platform:${orderBlockPlatformMatch[1]}`} orderID={decodeURIComponent(orderBlockPlatformMatch[1])} />;
    permissionPath = "/orders";
  } else if (orderDeliveriesMatch?.[1]) {
    page = <OrderDeliveriesPage key={orderDeliveriesMatch[1]} orderID={decodeURIComponent(orderDeliveriesMatch[1])} />;
    permissionPath = "/orders";
  } else if (orderDetailMatch?.[1]) {
    page = <OrderDetailPage key={orderDetailMatch[1]} orderID={decodeURIComponent(orderDetailMatch[1])} />;
    permissionPath = "/orders";
  } else if (operatorDeliveryDetailMatch?.[1]) {
    page = <OperatorOrderDeliveryDetailPage key={operatorDeliveryDetailMatch[1]} orderID={decodeURIComponent(operatorDeliveryDetailMatch[1])} />;
    permissionPath = "/operations/deliveries";
  } else if (ticketDetailMatch?.[1] && ticketDetailMatch[1] !== "new") {
    page = <TicketDetailPage key={ticketDetailMatch[1]} ticketID={decodeURIComponent(ticketDetailMatch[1])} />;
    permissionPath = "/support";
  } else if (reconciliationDifferenceResolveMatch?.[1] && reconciliationDifferenceResolveMatch[2]) {
    page = <ReconciliationDifferenceResolvePage key={`${reconciliationDifferenceResolveMatch[1]}:${reconciliationDifferenceResolveMatch[2]}`} batchID={decodeURIComponent(reconciliationDifferenceResolveMatch[1])} differenceID={decodeURIComponent(reconciliationDifferenceResolveMatch[2])} />;
    permissionPath = "/reports";
  } else if (reconciliationMatch?.[1] && reconciliationMatch[1] !== "new") {
    page = <ReconciliationDetailPage key={reconciliationMatch[1]} batchID={decodeURIComponent(reconciliationMatch[1])} />;
    permissionPath = "/reports";
  } else if (pathname === "/operations/managed-platforms/new") {
    page = <ManagedPlatformCreatePage />;
    permissionPath = "/operations/managed-platforms";
  } else if (pathname === "/operations/platforms/new") {
    page = <ManagedPlatformAccountCreatePage />;
    permissionPath = "/operations/platforms";
  } else if (pathname === "/operations/suppliers/new") {
    page = <ManagedSupplierCreatePage />;
    permissionPath = "/operations/suppliers";
  } else if (pathname === "/operations/assignments/new") {
    page = <AssignmentCreatePage />;
    permissionPath = "/operations/assignments";
  } else if (pathname === "/operations/team/new") {
    page = <TeamMemberCreatePage />;
    permissionPath = "/operations/team";
  } else if (teamMemberEditMatch?.[1]) {
    page = <TeamMemberEditPage key={teamMemberEditMatch[1]} userID={decodeURIComponent(teamMemberEditMatch[1])} />;
    permissionPath = "/operations/team";
  } else if (pathname === "/operations/reviews/new") {
    page = <ReviewActionPage />;
    permissionPath = "/operations/reviews";
  } else if (reviewActionMatch?.[1] && reviewActionMatch[2]) {
    page = <ReviewActionPage key={`${reviewActionMatch[1]}:${reviewActionMatch[2]}`} objectType={decodeURIComponent(reviewActionMatch[1])} objectID={decodeURIComponent(reviewActionMatch[2])} />;
    permissionPath = "/operations/reviews";
  } else if (paymentCancelMatch?.[1]) {
    page = <PaymentCancelPage key={paymentCancelMatch[1]} paymentID={decodeURIComponent(paymentCancelMatch[1])} />;
    permissionPath = "/finance/topups";
  } else if (paymentRefundRequestMatch?.[1]) {
    page = <PaymentRefundRequestPage key={paymentRefundRequestMatch[1]} paymentID={decodeURIComponent(paymentRefundRequestMatch[1])} />;
    permissionPath = "/finance/topups";
  } else if (paymentRefundReviewMatch?.[1] && paymentRefundReviewMatch[2]) {
    page = <PaymentRefundReviewPage key={`${paymentRefundReviewMatch[1]}:${paymentRefundReviewMatch[2]}`} refundID={decodeURIComponent(paymentRefundReviewMatch[1])} action={paymentRefundReviewMatch[2] as "complete" | "reject"} />;
    permissionPath = "/finance/refunds";
  } else if (adjustmentReviewMatch?.[1] && adjustmentReviewMatch[2]) {
    page = <BalanceAdjustmentReviewPage key={`${adjustmentReviewMatch[1]}:${adjustmentReviewMatch[2]}`} requestID={decodeURIComponent(adjustmentReviewMatch[1])} action={adjustmentReviewMatch[2] as "approve" | "reject"} />;
    permissionPath = "/finance/adjustments";
  } else if (invoiceProcessMatch?.[1]) {
    page = <InvoiceProcessPage key={invoiceProcessMatch[1]} invoiceID={decodeURIComponent(invoiceProcessMatch[1])} />;
    permissionPath = "/finance/invoices";
  } else if (withdrawalPayoutMatch?.[1]) {
    page = <WithdrawalPayoutPage key={withdrawalPayoutMatch[1]} withdrawalID={decodeURIComponent(withdrawalPayoutMatch[1])} />;
    permissionPath = "/finance/withdrawals";
  } else if (commissionPayoutMatch?.[1]) {
    page = <CommissionPayoutPage key={commissionPayoutMatch[1]} commissionID={decodeURIComponent(commissionPayoutMatch[1])} />;
    permissionPath = "/finance/commissions";
  } else if (customerProfileMatch?.[1] && customerProfileMatch[2]) {
    const customerType = customerProfileMatch[1] === "suppliers" ? "supplier" : "platform";
    page = <CustomerProfilePage key={`${customerType}:${customerProfileMatch[2]}`} customerType={customerType} organizationID={decodeURIComponent(customerProfileMatch[2])} />;
    permissionPath = `/operations/${customerProfileMatch[1]}`;
  } else if (credentialEditMatch?.[1] && credentialEditMatch[2]) {
    page = <CredentialEditPage key={`${credentialEditMatch[1]}:${credentialEditMatch[2]}`} listingID={decodeURIComponent(credentialEditMatch[1])} credentialID={decodeURIComponent(credentialEditMatch[2])} />;
    permissionPath = "/inventory";
  } else if (securityMemberEditMatch?.[1]) {
    page = <MemberEditPage key={securityMemberEditMatch[1]} userID={decodeURIComponent(securityMemberEditMatch[1])} />;
    permissionPath = "/security";
  } else if (systemUserStatusMatch?.[1]) {
    page = <SystemUserStatusPage key={systemUserStatusMatch[1]} userID={decodeURIComponent(systemUserStatusMatch[1])} />;
    permissionPath = "/system";
  } else if (systemOrganizationActionMatch?.[1] && systemOrganizationActionMatch[2]) {
    const organizationID = decodeURIComponent(systemOrganizationActionMatch[1]);
    page = systemOrganizationActionMatch[2] === "credit"
      ? <SystemOrganizationCreditPage key={`credit:${organizationID}`} organizationID={organizationID} />
      : <SystemOrganizationStatusPage key={`status:${organizationID}`} organizationID={organizationID} />;
    permissionPath = "/system";
  } else if (systemOperatorEditMatch?.[1]) {
    page = <TeamMemberEditPage key={systemOperatorEditMatch[1]} userID={decodeURIComponent(systemOperatorEditMatch[1])} workspace="system" />;
    permissionPath = "/system";
  } else if (pathname === "/inventory/new") {
    page = <SupplierListingCreatePage />;
    permissionPath = "/inventory";
  } else {
    const inventoryMatch = pathname.match(/^\/inventory\/([^/]+)(?:\/(keys|upload|edit|quote|rpm|evidence|state))?$/);
    if (inventoryMatch?.[1]) {
      const section = (inventoryMatch[2] === "edit" ? "settings" : inventoryMatch[2] ?? "overview") as SupplierListingSection;
      page = <SupplierListingPage key={`${inventoryMatch[1]}:${section}`} listingID={decodeURIComponent(inventoryMatch[1])} section={section} />;
      permissionPath = "/inventory";
    }
  }
  const content = page && !canAccessPath(subject, permissionPath) ? <Forbidden fallback={fallback} /> : page ?? <NotFound fallback={fallback} />;
  return <Shell pathname={pathname}><Suspense key={pathname} fallback={<WorkspaceLoading />}>{content}</Suspense></Shell>;
}

function defaultPath(_type: string) {
  return "/dashboard";
}

function NotFound({ fallback }: { fallback: string }) {
  return <div className="empty not-found"><span>404</span><h2>工作区不存在</h2><button className="button primary" onClick={() => navigate(fallback)}>返回总览</button></div>;
}

function Forbidden({ fallback }: { fallback: string }) {
  return <div className="empty not-found"><span>403</span><h2>当前职责无权访问</h2><p>该工作区未分配给当前组织角色。</p><button className="button primary" onClick={() => navigate(fallback)}>返回总览</button></div>;
}

function WorkspaceLoading() {
  return <div className="empty" role="status"><Spinner /><p>正在加载工作区</p></div>;
}
